import { Component, OnInit, Inject } from '@angular/core';
import { MatLegacyDialogRef as MatDialogRef, MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA } from '@angular/material/legacy-dialog';

import { DeviceDetectorService } from 'ngx-device-detector';

import { environment } from '../../../environments/environment';
import { ErrorHandler, HandleError } from '../../services/error-handler.service';

import { QuickPayPaymentService } from '../../services/quickpay-payment.service';
import { CompanyService } from '../../services/company.service';

import {
  QPaymentConfirmationDialogData,
  PaymentType,
  BankAccountType,
  CreditCardPaymentInformation,
  ACHPaymentInformation
} from '../../models/quickpay';

@Component({
  selector: 'app-payment-confirmation-dialog',
  templateUrl: './quick-pay-payment-confirmation-dialog.component.html',
  styleUrls: ['./quick-pay-payment-confirmation-dialog.component.scss']
})
export class QuickPayPaymentConfirmationDialogComponent implements OnInit {
  private _handleError: HandleError;
  private _logging = environment.logging;

  isBrowserIE = /msie\s|trident\//i.test(window.navigator.userAgent);

  showSpinner = false;
  showError = false;
  showCloseButton = true;

  paymentType = PaymentType;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: QPaymentConfirmationDialogData,
    private _errorHandler: ErrorHandler,
    private _paymentService: QuickPayPaymentService,
    private _companyService: CompanyService,
    private _deviceService: DeviceDetectorService,
    public dialogRef: MatDialogRef<QuickPayPaymentConfirmationDialogComponent>
  ) {
    this._handleError = _errorHandler.createHandleError('QuickPayPaymentConfirmationDialogComponent');
    this.submitPayment();
  }

  ngOnInit() {}

  submitPayment(): void {
    this.showSpinner = true;

    if (this.data.paymentDetail.paymentType === PaymentType.ElectronicCheck) {
      const achPaymentInformation: ACHPaymentInformation = {
        participantId: this.data.paymentDetail.participantId,
        noteIds: this.data.paymentDetail.noteIds,
        paymentAmount: this.data.paymentDetail.paymentAmount,
        paymentDueDay: this.data.paymentDetail.paymentDueDay,
        bankAccountNumber: this.data.paymentDetail.bankAccountNumber,
        bankAccountType: this.data.paymentDetail.bankAccountType === BankAccountType.Checking ? 'C' : 'S',
        bankRoutingNumber: this.data.paymentDetail.bankRoutingNumber,
        fullName: this.data.paymentDetail.name,
        addressLine1: this.data.paymentDetail.addressLine1,
        city: this.data.paymentDetail.city,
        state: this.data.paymentDetail.state,
        zipCode: this.data.paymentDetail.zipCode,
        userName: this.data.paymentDetail.userName,
        paymentRef: this.data.paymentDetail.paymentRef,
        checkPaymentId: this.data.paymentDetail.checkPaymentId,
        companyCode: this._companyService.getCompanyCode()
      };

      this._paymentService.setupACHPayment(achPaymentInformation).subscribe(
        result => {
          if (this._logging.serviceCallTraceLog) {
            console.log('QuickPayPaymentConfirmationDialogComponent.submitPayment.setupACHPayment :: result = ' + result);
          }
          if (this._logging.serviceDataTraceLog) {
            console.log('QuickPayPaymentConfirmationDialogComponent.submitPayment.setupACHPayment :: result = ' + result);
          }
          // 01/28/2020 RA - Return Payment Id to signify Record inserted into DB successfully
          // this.data.paymentDetail.confirmationCode = result !== null ? (result * -1).toString() : null;
          this.data.paymentDetail.confirmationCode = (result < 0 && result !== -999) ?
                                                      (result * -1).toString() : (result > 0 ? result.toString() : null);

          // 01/28/2020 RA - Return Payment Id to signify Record inserted into DB successfully
          if (Number(result) !== -999) {
            if (Number(result) < 0) {
              this._handleError('submitPayment.setupACHPayment',
                                'ACH Payment Succeeded but Email was not Sent - Email Server Down OR Could not retrieve Email Data',
                                this.data);
              this.data.paymentOk = true;
            } else {
              this.data.paymentOk = true;
            }
          } else {
            this._handleWorkflowError('ACH Payment could not be applied!');
          }
          this.submitPaymentComplete();
        },
        error => {
          this._handleWorkflowError('ACH Payment could not be applied!');
          this._handleError('submitPayment.setupACHPayment', error, this.data);
          this.submitPaymentComplete();
        }
      );
    } else if (this.data.paymentDetail.paymentType == PaymentType.CreditCard) {
      const creditCardPaymentInformation: CreditCardPaymentInformation = {
        participantId: this.data.paymentDetail.participantId,
        noteIds: this.data.paymentDetail.noteIds,
        paymentAmount: this.data.paymentDetail.paymentAmount,
        paymentDueDay: this.data.paymentDetail.paymentDueDay,
        loanProgram: this.data.paymentDetail.loanProgram,
        servicingCompany: this.data.paymentDetail.servicingCompany,
        fullName: this.data.paymentDetail.name,
        addressLine1: this.data.paymentDetail.addressLine1,
        city: this.data.paymentDetail.city,
        state: this.data.paymentDetail.state,
        zipCode: this.data.paymentDetail.zipCode,
        userName: this.data.paymentDetail.userName,
        paymentRef: this.data.paymentDetail.paymentRef,
        companyCode: this._companyService.getCompanyCode(),

        // Braintree tokenized response
        nonce: this.data.paymentDetail.nonce,
        partiFirstName: this.data.paymentDetail.partiFirstName,
        partiLastName: this.data.paymentDetail.partiLastName,
        partiEmail: this.data.paymentDetail.partiEmail,
        deviceData: this.data.paymentDetail.deviceData,
        creditCardBin: this.data.paymentDetail.creditCardBin,
        creditCardType: this.data.paymentDetail.creditCardType,
        lastFourCreditCard: this.data.paymentDetail.lastFourCreditCard,
        creditCardExpMonth: this.data.paymentDetail.creditCardExpMonth,
        creditCardExpYear: this.data.paymentDetail.creditCardExpYear,
        brainTreePaymentType: this.data.paymentDetail.brainTreePaymentType,
        brainTreeLenderProgramCode: this.data.paymentDetail.brainTreeLenderProgramCode,
        paymentType: this.data.paymentDetail.paymentType
      };

      this._paymentService.setupCreditCardPayment(creditCardPaymentInformation).subscribe(
        result => {
          if (this._logging.serviceCallTraceLog) {
            console.log(
              'QuickPayPaymentConfirmationDialogComponent.submitPayment.setupCreditCardPayment :: result.transactionResponseResult = ' +
                result.transactionResponseResult
            );
          }
          if (this._logging.serviceDataTraceLog) {
            console.log('QuickPayPaymentConfirmationDialogComponent.submitPayment.setupCreditCardPayment :: result = ' + JSON.stringify(result));
          }

          this.data.paymentDetail.confirmationCode = result.confirmationCode;
          this.data.paymentDetail.responseMessage = result.transactionResponseMessage;
          this.data.paymentDetail.responseCode = result.transactionResponseResult !== null ? result.transactionResponseResult.toString() : null;

          // Braintree checking for approved result
          if (result != null && result.transactionResponseResult !== null &&
              result.transactionResponseResult === 1000) {
              this.data.paymentOk = true;

              let emailResult = (result != null && result.emailProcessingResult != null) ? result.emailProcessingResult : 0;
              if (emailResult != 0) {
                if (emailResult === -1) {
                  this._handleError('submitPayment.setupCreditCardPayment',
                                    'CC Payment Succeeded but no Email Sent - Email Info Error', this.data);
                } else if (emailResult === -2) {
                  this._handleError('submitPayment.setupCreditCardPayment',
                                    'CC Payment Succeeded but no Email Sent - Email not available', this.data);
                } else {
                  this._handleError('submitPayment.setupCreditCardPayment',
                                    'CC Payment Succeeded but no Email Sent - Email Server down!', this.data);
                }
              }
          } else {
              this._handleWorkflowError('Credit Card Payment could not be applied!');
          }
          this.submitPaymentComplete();

        },
        error => {
          this._handleWorkflowError('Credit Card Payment could not be applied!');
          this._handleError('submitPayment.setupCreditCardPayment', error, this.data);
          this.submitPaymentComplete();
        }
      );
    } else if (this.data.paymentDetail.paymentType == PaymentType.PayPal) {
        const creditCardPaymentInformation: CreditCardPaymentInformation = {
          participantId: this.data.paymentDetail.participantId,
          noteIds: this.data.paymentDetail.noteIds,
          paymentAmount: this.data.paymentDetail.paymentAmount,
          paymentDueDay: this.data.paymentDetail.paymentDueDay,
          loanProgram: this.data.paymentDetail.loanProgram,
          servicingCompany: this.data.paymentDetail.servicingCompany,
          fullName: this.data.paymentDetail.name,
          addressLine1: this.data.paymentDetail.addressLine1,
          city: this.data.paymentDetail.city,
          state: this.data.paymentDetail.state,
          zipCode: this.data.paymentDetail.zipCode,
          userName: this.data.paymentDetail.userName,
          paymentRef: this.data.paymentDetail.paymentRef,
          companyCode: this._companyService.getCompanyCode(),

          // Braintree tokenized response
          nonce: this.data.paymentDetail.nonce,
          partiFirstName: this.data.paymentDetail.partiFirstName,
          partiLastName: this.data.paymentDetail.partiLastName,
          partiEmail: this.data.paymentDetail.partiEmail,
          deviceData: this.data.paymentDetail.deviceData,
          creditCardBin: null,
          creditCardType: null,
          lastFourCreditCard: null,
          creditCardExpMonth: null,
          creditCardExpYear: null,
          brainTreePaymentType: this.data.paymentDetail.brainTreePaymentType,
          brainTreeLenderProgramCode: this.data.paymentDetail.brainTreeLenderProgramCode,
          paymentType: this.data.paymentDetail.paymentType
        };

        this._paymentService.setupPayPalPayment(creditCardPaymentInformation).subscribe(
          result => {
            if (this._logging.serviceCallTraceLog) {
              console.log(
                'QuickPayPaymentConfirmationDialogComponent.submitPayment.setupPayPalPayment :: result.transactionResponseResult = ' +
                  result.transactionResponseResult
              );
            }
            if (this._logging.serviceDataTraceLog) {
              console.log('QuickPayPaymentConfirmationDialogComponent.submitPayment.setupPayPalPayment :: result = ' + JSON.stringify(result));
            }

            this.data.paymentDetail.confirmationCode = result.confirmationCode;
            this.data.paymentDetail.responseMessage = result.transactionResponseMessage;
            this.data.paymentDetail.responseCode = result.transactionResponseResult !== null ? result.transactionResponseResult.toString() : null;

            // Braintree checking for approved result
            if (result != null && result.transactionResponseResult !== null &&
                result.transactionResponseResult === 1000) {
                this.data.paymentOk = true;

                let emailResult = (result != null && result.emailProcessingResult != null) ? result.emailProcessingResult : 0;
                if (emailResult != 0) {
                  if (emailResult === -1) {
                    this._handleError('QuickPaysubmitPayment.setupPayPalPayment',
                                      'PayPal Payment Succeeded but no Email Sent - Email Info Error', this.data);
                  } else if (emailResult === -2) {
                    this._handleError('QuickPaysubmitPayment.setupPayPalPayment',
                                      'PayPal Payment Succeeded but no Email Sent - Email not available', this.data);
                  } else {
                    this._handleError('QuickPaysubmitPayment.setupPayPalPayment',
                                      'PayPal Payment Succeeded but no Email Sent - Email Server down!', this.data)
                  }
                }
            } else {
                this._handleWorkflowError('Paypal Payment could not be applied!');
            }
            this.submitPaymentComplete();

          },
          error => {
            this._handleWorkflowError('Paypal Payment could not be applied!');
            this._handleError('QuickPaysubmitPayment.setupPayPalPayment', error, this.data);
            this.submitPaymentComplete();
          }
        );
  } else if (this.data.paymentDetail.paymentType == PaymentType.GooglePay) {
      const creditCardPaymentInformation: CreditCardPaymentInformation = {
        participantId: this.data.paymentDetail.participantId,
        noteIds: this.data.paymentDetail.noteIds,
        paymentAmount: this.data.paymentDetail.paymentAmount,
        paymentDueDay: this.data.paymentDetail.paymentDueDay,
        loanProgram: this.data.paymentDetail.loanProgram,
        servicingCompany: this.data.paymentDetail.servicingCompany,
        fullName: this.data.paymentDetail.name,
        addressLine1: this.data.paymentDetail.addressLine1,
        city: this.data.paymentDetail.city,
        state: this.data.paymentDetail.state,
        zipCode: this.data.paymentDetail.zipCode,
        userName: this.data.paymentDetail.userName,
        paymentRef: this.data.paymentDetail.paymentRef,
        companyCode: this._companyService.getCompanyCode(),

        // Braintree tokenized response
        nonce: this.data.paymentDetail.nonce,
        partiFirstName: this.data.paymentDetail.partiFirstName,
        partiLastName: this.data.paymentDetail.partiLastName,
        partiEmail: this.data.paymentDetail.partiEmail,
        deviceData: this.data.paymentDetail.deviceData,
        creditCardBin: null,
        creditCardType: null,
        lastFourCreditCard: null,
        creditCardExpMonth: null,
        creditCardExpYear: null,
        brainTreePaymentType: this.data.paymentDetail.brainTreePaymentType,
        brainTreeLenderProgramCode: this.data.paymentDetail.brainTreeLenderProgramCode,
        paymentType: this.data.paymentDetail.paymentType
      };

      this._paymentService.setupGooglePayPayment(creditCardPaymentInformation).subscribe(
        result => {
          if (this._logging.serviceCallTraceLog) {
            console.log(
              'QuickPayPaymentConfirmationDialogComponent.submitPayment.setupGooglePayPayment :: result.transactionResponseResult = ' +
                result.transactionResponseResult
            );
          }
          if (this._logging.serviceDataTraceLog) {
            console.log('QuickPayPaymentConfirmationDialogComponent.submitPayment.setupGooglePayPayment :: result = ' + JSON.stringify(result));
          }

          this.data.paymentDetail.confirmationCode = result.confirmationCode;
          this.data.paymentDetail.responseMessage = result.transactionResponseMessage;
          this.data.paymentDetail.responseCode = result.transactionResponseResult !== null ? result.transactionResponseResult.toString() : null;

          // Braintree checking for approved result
          if (result != null && result.transactionResponseResult !== null &&
              result.transactionResponseResult === 1000) {
              this.data.paymentOk = true;

              let emailResult = (result != null && result.emailProcessingResult != null) ? result.emailProcessingResult : 0;
              if (emailResult != 0) {
                if (emailResult === -1) {
                  this._handleError('QuickPaysubmitPayment.setupGooglePayPayment',
                                    'Google Pay Payment Succeeded but no Email Sent - Email Info Error', this.data);
                } else if (emailResult === -2) {
                  this._handleError('QuickPaysubmitPayment.setupGooglePayPayment',
                                    'Google Pay Payment Succeeded but no Email Sent - Email not available', this.data);
                } else {
                  this._handleError('QuickPaysubmitPayment.setupGooglePayPayment',
                                    'Google Pay Payment Succeeded but no Email Sent - Email Server down!', this.data);
                }
              }
          } else {
              this._handleWorkflowError('Google Pay Payment could not be applied!');
          }
          this.submitPaymentComplete();

        },
        error => {
          this._handleWorkflowError('Google Pay Payment could not be applied!');
          this._handleError('QuickPaysubmitPayment.setupGooglePayPayment', error, this.data);
          this.submitPaymentComplete();
        }
      );
  } else if (this.data.paymentDetail.paymentType == PaymentType.ApplePay) {
      const creditCardPaymentInformation: CreditCardPaymentInformation = {
        participantId: this.data.paymentDetail.participantId,
        noteIds: this.data.paymentDetail.noteIds,
        paymentAmount: this.data.paymentDetail.paymentAmount,
        paymentDueDay: this.data.paymentDetail.paymentDueDay,
        loanProgram: this.data.paymentDetail.loanProgram,
        servicingCompany: this.data.paymentDetail.servicingCompany,
        fullName: this.data.paymentDetail.name,
        addressLine1: this.data.paymentDetail.addressLine1,
        city: this.data.paymentDetail.city,
        state: this.data.paymentDetail.state,
        zipCode: this.data.paymentDetail.zipCode,
        userName: this.data.paymentDetail.userName,
        paymentRef: this.data.paymentDetail.paymentRef,
        companyCode: this._companyService.getCompanyCode(),

        // Braintree tokenized response
        nonce: this.data.paymentDetail.nonce,
        partiFirstName: this.data.paymentDetail.partiFirstName,
        partiLastName: this.data.paymentDetail.partiLastName,
        partiEmail: this.data.paymentDetail.partiEmail,
        deviceData: this.data.paymentDetail.deviceData,
        creditCardBin: null,
        creditCardType: null,
        lastFourCreditCard: null,
        creditCardExpMonth: null,
        creditCardExpYear: null,
        brainTreePaymentType: this.data.paymentDetail.brainTreePaymentType,
        brainTreeLenderProgramCode: this.data.paymentDetail.brainTreeLenderProgramCode,
        paymentType: this.data.paymentDetail.paymentType
      };

      this._paymentService.setupApplePayPayment(creditCardPaymentInformation).subscribe(
        result => {
          if (this._logging.serviceCallTraceLog) {
            console.log(
              'QuickPayPaymentConfirmationDialogComponent.submitPayment.setupApplePayPayment :: result.transactionResponseResult = ' +
                result.transactionResponseResult
            );
          }
          if (this._logging.serviceDataTraceLog) {
            console.log('QuickPayPaymentConfirmationDialogComponent.submitPayment.setupApplePayPayment :: result = ' + JSON.stringify(result));
          }

          this.data.paymentDetail.confirmationCode = result.confirmationCode;
          this.data.paymentDetail.responseMessage = result.transactionResponseMessage;
          this.data.paymentDetail.responseCode = result.transactionResponseResult !== null ? result.transactionResponseResult.toString() : null;

          // Braintree checking for approved result
          if (result != null && result.transactionResponseResult !== null &&
              result.transactionResponseResult === 1000) {
              this.data.paymentOk = true;

              let emailResult = (result != null && result.emailProcessingResult != null) ? result.emailProcessingResult : 0;
              if (emailResult != 0) {
                if (emailResult === -1) {
                  this._handleError('QuickPaysubmitPayment.setupApplePayPayment',
                                    'Apple Pay Payment Succeeded but no Email Sent - Email Info Error', this.data);
                } else if (emailResult === -2) {
                  this._handleError('QuickPaysubmitPayment.setupApplePayPayment',
                                    'Apple Pay Payment Succeeded but no Email Sent - Email not available', this.data);
                } else {
                  this._handleError('QuickPaysubmitPayment.setupApplePayPayment',
                                    'Apple Pay Payment Succeeded but no Email Sent - Email Server down!', this.data);
                }
              }
          } else {
              this._handleWorkflowError('Apple Pay Payment could not be applied!');
          }
          this.submitPaymentComplete();

        },
        error => {
          this._handleWorkflowError('Apple Pay Payment could not be applied!');
          this._handleError('QuickPaysubmitPayment.setupApplePayPayment', error, this.data);
          this.submitPaymentComplete();
        }
      );
    }
  }

  submitPaymentComplete(): void {
    this.showSpinner = false;
  }

  maskCreditCardNumber(creditCardNumber: string): string {
    return creditCardNumber.replace(/\d(?=\d{4})/g, '*');
  }

  onCloseClick() {
    this.dialogRef.close(this.data);
  }

  isMobileDevice(): boolean {
    return !this._deviceService.isDesktop();
  }

  private _handleWorkflowError(errorMessage: string): void {
    this.showError = true;
    this.data.paymentOk = false;
    this.data.paymentError = errorMessage;
  }
}
